/**
 * The requirement check — "is this enough to judge a candidate on?"
 *
 * After a recruiter types or drops a requirement, the wizard asks POST /requisitions/requirement/
 * check whether the text is specific enough to build a fair interview from, and shows what it
 * came back with in one dialog: a verdict, the areas the text is silent on, a handful of
 * SUGGESTED lines the recruiter can accept or decline with one click, a few questions to answer,
 * and room to add their own lines. Whatever they accept is appended to the requirement itself as
 * a "Clarifications" block — so the same text the question generator quotes, the phrase checker
 * verifies against and the résumé scorer reads is the text the recruiter confirmed. Nothing here
 * is applied silently: a suggestion is a proposal until the recruiter says yes.
 *
 * This file is the contract (the response shape and its limits) plus the two pure helpers that
 * compose the block and merge it into the requirement — shared so the wizard, the API and the
 * tests read one definition.
 */
import { z } from "zod";
export const REQUIREMENT_CHECK_VERDICTS = ["enough", "thin", "insufficient"];
export const REQUIREMENT_CHECK_LIMITS = {
    /** Below this the text is a title line, not a requirement — the check does not run. */
    minChars: 80,
    maxChars: 20000,
    maxMissing: 6,
    maxSuggestions: 6,
    maxQuestions: 4,
    maxItemLen: 240,
    maxSummaryLen: 400,
    maxManualLines: 8,
    maxAnswerLen: 400,
};
const idSchema = z.string().trim().min(1).max(60);
const lineSchema = z.string().trim().min(8).max(REQUIREMENT_CHECK_LIMITS.maxItemLen);
export const requirementCheckMissingSchema = z.object({
    id: idSchema,
    /** Short area name, e.g. "Years of experience". */
    label: z.string().trim().min(2).max(80),
    /** Why an interview built without it would be weaker — one sentence. */
    why: z.string().trim().max(REQUIREMENT_CHECK_LIMITS.maxItemLen),
});
/** A line the recruiter can accept as-is into the requirement. Written as a requirement line. */
export const requirementCheckSuggestionSchema = z.object({
    id: idSchema,
    text: lineSchema,
    /** Which missing area it fills, if any (the id of a `missing` entry). */
    area: idSchema.optional(),
});
/** A question the recruiter answers in their own words; the answer becomes a requirement line. */
export const requirementCheckQuestionSchema = z.object({
    id: idSchema,
    question: lineSchema,
    why: z.string().trim().max(REQUIREMENT_CHECK_LIMITS.maxItemLen).optional(),
});
export const requirementCheckSchema = z.object({
    verdict: z.enum(REQUIREMENT_CHECK_VERDICTS),
    /** One or two sentences, addressed to the recruiter, saying what the text does and does not give. */
    summary: z.string().trim().max(REQUIREMENT_CHECK_LIMITS.maxSummaryLen),
    missing: z.array(requirementCheckMissingSchema).max(REQUIREMENT_CHECK_LIMITS.maxMissing),
    suggestions: z.array(requirementCheckSuggestionSchema).max(REQUIREMENT_CHECK_LIMITS.maxSuggestions),
    questions: z.array(requirementCheckQuestionSchema).max(REQUIREMENT_CHECK_LIMITS.maxQuestions),
    /** Which engine answered: the deterministic rules alone, or Gemini over the rules. */
    source: z.enum(["rules", "gemini"]),
});
/** What the recruiter decided, stored on the requisition (taxonomy_enrichment.requirementCheck). */
export const requirementCheckDecisionSchema = z.object({
    verdict: z.enum(REQUIREMENT_CHECK_VERDICTS),
    source: z.enum(["rules", "gemini"]).optional(),
    accepted: z.array(idSchema).max(REQUIREMENT_CHECK_LIMITS.maxSuggestions).optional(),
    declined: z.array(idSchema).max(REQUIREMENT_CHECK_LIMITS.maxSuggestions).optional(),
    answered: z.record(z.string().max(REQUIREMENT_CHECK_LIMITS.maxAnswerLen)).optional(),
    manual: z.array(z.string().max(REQUIREMENT_CHECK_LIMITS.maxItemLen)).max(REQUIREMENT_CHECK_LIMITS.maxManualLines).optional(),
    /** "skipped" when the dialog was shown and the recruiter continued without adding anything. */
    outcome: z.enum(["applied", "skipped"]),
});
/** The heading of the block the wizard appends to the requirement. Fixed, so it can be found again. */
export const CLARIFICATIONS_HEADING = "Clarifications confirmed by the recruiter:";
const oneLine = (s) => s.replace(/\s+/g, " ").trim();
/**
 * Turn what the recruiter chose into the lines of the block: accepted suggestions as written,
 * answered questions as "question — answer", manual lines as typed. Deduplicated (case- and
 * whitespace-insensitive), empties dropped, every line clipped to the item limit, hard-capped in
 * count so a pasted essay cannot become forty bullets.
 */
export function composeClarifications(input) {
    const out = [];
    const seen = new Set();
    const push = (raw) => {
        const line = oneLine(raw).slice(0, REQUIREMENT_CHECK_LIMITS.maxItemLen);
        if (line.length < 2)
            return;
        const key = line.toLowerCase();
        if (seen.has(key))
            return;
        seen.add(key);
        out.push(line);
    };
    for (const k of input.kept ?? [])
        push(k);
    const keptCount = out.length;
    for (const s of input.accepted)
        push(s);
    for (const qa of input.answers) {
        const a = oneLine(qa.answer);
        if (!a)
            continue;
        push(`${oneLine(qa.question).replace(/[?:]+$/, "")}: ${a}`);
    }
    for (const m of input.manual)
        push(m);
    const cap = keptCount + REQUIREMENT_CHECK_LIMITS.maxSuggestions + REQUIREMENT_CHECK_LIMITS.maxQuestions + REQUIREMENT_CHECK_LIMITS.maxManualLines;
    return out.slice(0, cap);
}
/**
 * Where the existing block sits in a requirement, if one does: the heading line plus every
 * following bullet line (a run of "- " lines, blank lines allowed inside it). Text after the run
 * — a recruiter may keep typing below — is left alone.
 */
function findClarificationsBlock(text) {
    const idx = text.indexOf(CLARIFICATIONS_HEADING);
    if (idx < 0)
        return null;
    // Start at the beginning of the heading's line.
    const start = text.lastIndexOf("\n", idx - 1) + 1;
    const lines = text.slice(idx).split("\n");
    let consumed = lines[0].length; // the heading line
    for (let i = 1; i < lines.length; i++) {
        const l = lines[i];
        if (l.trim() === "" || l.trimStart().startsWith("- ")) {
            consumed += 1 + l.length;
            continue;
        }
        break;
    }
    return { start, end: idx + consumed };
}
/**
 * Merge the lines into the requirement as ONE block. Idempotent: a second application replaces
 * the previous block rather than stacking another; an empty `lines` removes it. Everything the
 * recruiter wrote outside the block is preserved byte for byte.
 */
export function withClarifications(jobDescription, lines) {
    const existing = findClarificationsBlock(jobDescription);
    const before = existing ? jobDescription.slice(0, existing.start) : jobDescription;
    const after = existing ? jobDescription.slice(existing.end) : "";
    const body = before.replace(/\s+$/, "");
    const tail = after.replace(/^\s+/, "");
    if (lines.length === 0)
        return [body, tail].filter(Boolean).join("\n\n").trim();
    const block = [CLARIFICATIONS_HEADING, ...lines.map((l) => `- ${l}`)].join("\n");
    return [body, block, tail].filter(Boolean).join("\n\n").trim();
}
/** Whether a requirement already carries a block (so the wizard can say "update" rather than "add"). */
export function hasClarifications(jobDescription) {
    return findClarificationsBlock(jobDescription) !== null;
}
/**
 * The lines of the block as the requirement holds them NOW — hand edits included, one entry per
 * "- " bullet, blank bullets dropped. The wizard feeds these back as `kept` so a second pass
 * through the dialog (a re-check after the first application, or the recruiter reopening it)
 * updates the block instead of replacing it with only the newest choices.
 */
export function readClarifications(jobDescription) {
    const block = findClarificationsBlock(jobDescription);
    if (!block)
        return [];
    return jobDescription
        .slice(block.start, block.end)
        .split("\n")
        .slice(1) // the heading line
        .map((l) => l.trimStart())
        .filter((l) => l.startsWith("- "))
        .map((l) => oneLine(l.slice(2)))
        .filter((l) => l.length > 0);
}
//# sourceMappingURL=requirementCheck.js.map